import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { listAuditLog } from "../api";
import { formatDateTime } from "../utils/format";
import { apiError } from "../utils/meta";

const MONO = "'SF Mono', Menlo, monospace";

const ACTION_COLOR = {
    create: "var(--success)",
    update: "var(--info)",
    delete: "var(--error)",
};

const actionColor = (action) => {
    const key = Object.keys(ACTION_COLOR).find((k) =>
        String(action ?? "").toLowerCase().includes(k),
    );
    return key ? ACTION_COLOR[key] : "var(--text-strong-muted)";
};

function ChangesCell({ changes }) {
    if (!changes || (typeof changes === "object" && Object.keys(changes).length === 0)) {
        return (
            <Typography sx={{ fontSize: 13, color: "var(--text-tertiary)" }}>
                —
            </Typography>
        );
    }
    const json = JSON.stringify(changes, null, 2);
    return (
        <Tooltip
            arrow
            placement="left"
            componentsProps={{
                tooltip: {
                    sx: {
                        maxWidth: 480,
                        background: "#171715",
                        border: "1px solid #383632",
                        p: 1.5,
                    },
                },
            }}
            title={
                <Typography
                    component="pre"
                    sx={{
                        m: 0,
                        fontFamily: MONO,
                        fontSize: 11.5,
                        lineHeight: 1.6,
                        color: "#d8d5cf",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 360,
                        overflowY: "auto",
                    }}
                >
                    {json}
                </Typography>
            }
        >
            <Typography
                sx={{
                    fontSize: 12.5,
                    fontFamily: MONO,
                    color: "var(--text-secondary)",
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "help",
                    textDecoration: "underline dotted var(--border-strong)",
                    textUnderlineOffset: "3px",
                }}
            >
                {json.replace(/\s+/g, " ")}
            </Typography>
        </Tooltip>
    );
}

export default function AdminAudit() {
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
    });
    const [entityType, setEntityType] = useState("");
    const [action, setAction] = useState("");
    const [loading, setLoading] = useState(false);

    const load = useCallback(
        async (page = 1, limit = 25, filters = {}) => {
            setLoading(true);
            try {
                const params = { page, limit };
                if (filters.entityType) params.entityType = filters.entityType;
                if (filters.action) params.action = filters.action;
                const { data } = await listAuditLog(params);
                setRows(data.data ?? []);
                setPagination(data.pagination ?? { page, limit, total: 0 });
            } catch (err) {
                toast.error(apiError(err, "Failed to load the audit log"));
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        const t = setTimeout(
            () => load(1, pagination.limit, { entityType, action }),
            entityType || action ? 350 : 0,
        );
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityType, action, load]);

    const columns = useMemo(
        () => [
            {
                key: "userName",
                label: "Who",
                render: (row) => (
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {row.userName || "System"}
                    </Typography>
                ),
            },
            {
                key: "action",
                label: "Action",
                render: (row) => {
                    const color = actionColor(row.action);
                    return (
                        <Chip
                            size="small"
                            label={row.action}
                            sx={{
                                height: 22,
                                fontSize: 11.5,
                                fontWeight: 600,
                                fontFamily: MONO,
                                color,
                                background: "transparent",
                                border: `1px solid ${color}`,
                            }}
                        />
                    );
                },
            },
            { key: "entityType", label: "Entity type" },
            {
                key: "entityId",
                label: "Entity id",
                render: (row) => (
                    <Typography
                        sx={{
                            fontSize: 12.5,
                            fontFamily: MONO,
                            color: "var(--text-secondary)",
                        }}
                    >
                        {row.entityId ?? "—"}
                    </Typography>
                ),
            },
            {
                key: "changes",
                label: "Changes",
                render: (row) => <ChangesCell changes={row.changes} />,
            },
            {
                key: "createdAt",
                label: "When",
                render: (row) => formatDateTime(row.createdAt),
            },
        ],
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Audit Log"
                subtitle="Every change in the workspace — who did what, and when"
            />

            <Stack sx={{ gap: 2 }}>
                <Stack sx={{ flexDirection: "row", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                        placeholder="Filter by entity type…"
                        value={entityType}
                        onChange={(e) => setEntityType(e.target.value)}
                        sx={{ maxWidth: 260 }}
                    />
                    <TextField
                        placeholder="Filter by action…"
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        sx={{ maxWidth: 260 }}
                    />
                </Stack>

                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={(page) =>
                        load(page, pagination.limit, { entityType, action })
                    }
                    onLimitChange={(limit) =>
                        load(1, limit, { entityType, action })
                    }
                    emptyMessage="No audit entries match these filters"
                />
            </Stack>
        </Stack>
    );
}
